import {
  totalScoreAtLeastMessage,
  totalScoreAtMostMessage,
  totalScoreFieldName,
} from "../data/copy/usClassroomTerms";

const PYDANTIC_VALIDATION_HEADER = /validation error/i;

const FIELD_BLOCK_PATTERN = /^(\w+)\s*\n\s*(.+?)(?:\s*\[type=|\s*$)/gm;

// Markers that indicate a message is a raw framework/validation string that
// should never be shown to end users verbatim.
const RAW_PYDANTIC_MARKER =
  /should have at (?:least|most)|Field required|Input should be|value is not a valid|\[type=|validation error/i;

const FRIENDLY_FIELD_NAMES = {
  curriculum: "Curriculum",
  grade: "Grade",
  subject: "Subject",
  topic: "Topic",
  deckType: "Deck type",
  deck_type: "Deck type",
  duration: "Duration",
  durationMinutes: "Duration",
  duration_minutes: "Duration",
  totalMarks: totalScoreFieldName(),
  total_marks: totalScoreFieldName(),
  style: "Style",
  pastedContent: "Pasted content",
  pasted_content: "Pasted content",
  regenerationInstructions: "Regeneration instructions",
  regeneration_instructions: "Regeneration instructions",
};

const GENERIC_MISSING_MESSAGE =
  "Some required details are missing. Please review your inputs and try again.";

function friendlyFieldName(field) {
  if (!field) {
    return null;
  }

  const base = field.replace(/\[\d+\]$/, "");
  if (FRIENDLY_FIELD_NAMES[base]) {
    return FRIENDLY_FIELD_NAMES[base];
  }

  const spaced = base
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) {
    return null;
  }

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Turns a single (already de-typed) Pydantic constraint message into friendly
// copy. Returns null when the message is not a recognized constraint.
function rewriteConstraint(field, cleaned) {
  const friendly = friendlyFieldName(field);

  const greaterThanEqual = cleaned.match(
    /(?:Input )?should be greater than or equal to (\d+)/i,
  );
  if (greaterThanEqual) {
    const limit = greaterThanEqual[1];
    if (field === "durationMinutes" || field === "duration_minutes") {
      return `Duration must be at least ${limit} minutes.`;
    }
    if (field === "totalMarks" || field === "total_marks") {
      return totalScoreAtLeastMessage(limit);
    }
    return friendly
      ? `${friendly} must be at least ${limit}.`
      : `Must be at least ${limit}.`;
  }

  const lessThanEqual = cleaned.match(
    /(?:Input )?should be less than or equal to (\d+)/i,
  );
  if (lessThanEqual) {
    const limit = lessThanEqual[1];
    if (field === "durationMinutes" || field === "duration_minutes") {
      return `Duration must be at most ${limit} minutes.`;
    }
    if (field === "totalMarks" || field === "total_marks") {
      return totalScoreAtMostMessage(limit);
    }
    return friendly
      ? `${friendly} must be at most ${limit}.`
      : `Must be at most ${limit}.`;
  }

  const tooLong = cleaned.match(/should have at most (\d+) character/i);
  if (tooLong) {
    const limit = tooLong[1];
    return friendly
      ? `${friendly} must be at most ${limit} characters.`
      : `Must be at most ${limit} characters.`;
  }

  const tooShort = cleaned.match(/should have at least \d+ character/i);
  if (tooShort) {
    return friendly ? `${friendly} is required.` : GENERIC_MISSING_MESSAGE;
  }

  if (/^field required$/i.test(cleaned)) {
    return friendly ? `${friendly} is required.` : GENERIC_MISSING_MESSAGE;
  }

  return null;
}

function humanizePydanticFieldMessage(field, rawMessage) {
  const cleaned = rawMessage.replace(/\s*\[type=.*$/, "").trim();
  return rewriteConstraint(field, cleaned) ?? cleaned;
}

// Handles single-line validation messages that may carry a `field: message`
// prefix (as produced by the API client when flattening FastAPI 422 details)
// or be a bare Pydantic constraint string.
function humanizeBareValidationMessage(message) {
  const withoutType = message.replace(/\s*\[type=.*$/i, "").trim();

  let field = null;
  let core = withoutType;
  const colonIndex = withoutType.indexOf(":");
  if (colonIndex > -1) {
    const maybeField = withoutType.slice(0, colonIndex).trim();
    if (/^[\w.]+(?:\[\d+\])?$/.test(maybeField)) {
      field = maybeField;
      core = withoutType.slice(colonIndex + 1).trim();
    }
  }

  return rewriteConstraint(field, core);
}

function looksLikeRawPydantic(message) {
  return RAW_PYDANTIC_MARKER.test(message);
}

export function parsePydanticFieldErrors(message) {
  if (typeof message !== "string" || !PYDANTIC_VALIDATION_HEADER.test(message)) {
    return null;
  }

  const errors = {};
  FIELD_BLOCK_PATTERN.lastIndex = 0;

  let match = FIELD_BLOCK_PATTERN.exec(message);
  while (match) {
    const field = match[1];
    errors[field] = {
      field,
      code: "INVALID",
      message: humanizePydanticFieldMessage(field, match[2]),
    };
    match = FIELD_BLOCK_PATTERN.exec(message);
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

export function humanizeGenerationErrorMessage(message, fallback) {
  const parsed = parsePydanticFieldErrors(message);
  if (parsed) {
    const firstError = Object.values(parsed)[0];
    if (firstError?.message) {
      return firstError.message;
    }
  }

  if (typeof message === "string" && message.trim()) {
    const trimmed = message.trim();

    const humanized = humanizeBareValidationMessage(trimmed);
    if (humanized) {
      return humanized;
    }

    // Never surface a raw technical validation string we couldn't rewrite.
    if (looksLikeRawPydantic(trimmed)) {
      return fallback;
    }

    return trimmed;
  }

  return fallback;
}
